import { colors } from "constants/colors";
import { useHideBottomFixedForKeyboard } from "hooks/useHideBottomFixedForKeyboard";
import { FullScreenModalContext } from "hooks/useFullScreenModal";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import styled from "styled-components";

const TRANSITION_ENTER_MS = 300;
const TRANSITION_EXIT_MS = 100;

interface Props {
  children: ReactNode | ((requestClose: () => void) => ReactNode);
  className?: string;
  onExitComplete?: () => void;
}

// 풀스크린 모달 래퍼 — 아래→위 진입 / 위→아래 퇴장 (배경은 자식 컨텐츠에서 지정)
const FullScreenModal = ({ children, className, onExitComplete }: Props) => {
  const OFF_SCREEN_Y_VH = 100;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const isInputFocused = useHideBottomFixedForKeyboard(wrapperRef);

  const [yVh, setYVh] = useState(OFF_SCREEN_Y_VH);
  const [exiting, setExiting] = useState(false);

  const requestClose = useCallback(() => {
    setExiting(true);
    setYVh(OFF_SCREEN_Y_VH);
  }, []);

  const handleExitComplete = useCallback(() => {
    onExitComplete?.();
  }, [onExitComplete]);

  const contextValue = useMemo(
    () => ({ requestClose, isInputFocused }),
    [requestClose, isInputFocused],
  );

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setYVh(0));
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (!exiting) return;
    const t = window.setTimeout(handleExitComplete, TRANSITION_EXIT_MS);
    return () => window.clearTimeout(t);
  }, [exiting, handleExitComplete]);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  return (
    <FullScreenModalContext.Provider value={contextValue}>
      <StyledModalWrapper
        ref={wrapperRef}
        className={className}
        $yVh={yVh}
        $exiting={exiting}
      >
        {typeof children === "function" ? children(requestClose) : children}
      </StyledModalWrapper>
    </FullScreenModalContext.Provider>
  );
};

export default FullScreenModal;

const StyledModalWrapper = styled.div<{
  $yVh: number;
  $exiting: boolean;
}>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  inset: 0;
  width: 100%;
  min-height: 100%;
  min-height: 100dvh;
  z-index: 5;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  background-color: ${colors.white};
  transform: translate3d(0, ${({ $yVh }) => $yVh}dvh, 0);
  transition: transform
    ${({ $exiting }) => ($exiting ? TRANSITION_EXIT_MS : TRANSITION_ENTER_MS)}ms
    cubic-bezier(0.32, 0.72, 0, 1);
`;
